import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setApiMode, store } from "../lib/api";

const Ctx = createContext(null);
export const useFamily = () => useContext(Ctx);
let demoStart = null;
const startDemoOnce = () => {
  if (!demoStart) {
    demoStart = api.post("/demo/start").then((r) => {
      store.setDemo(r.data);
      return r.data;
    }).finally(() => {
      setTimeout(() => { demoStart = null; }, 2000);
    });
  }
  return demoStart;
};

export function FamilyProvider({ mode, children }) {
  const [fid, setFid] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [helperId, setHelperId] = useState(null);
  const base = mode === "demo" ? "/demo" : mode === "helper" ? "/helper" : "/app";

  const load = useCallback(async (id) => {
    const r = await api.get(`/families/${id}`);
    setData(r.data);
  }, []);

  useEffect(() => {
    setApiMode(mode);
    const boot = async () => {
      try {
        let id;
        if (mode === "demo") {
          const d = store.getDemo();
          if (d) {
            try {
              await load(d.family_id);
              setFid(d.family_id);
              return;
            } catch {
              store.clearDemo();
            }
          }
          const started = await startDemoOnce();
          id = started.family_id;
        } else if (mode === "helper") {
          const r = await api.get("/helper/me");
          id = r.data.family_id;
          setHelperId(r.data.helper_id);
        } else {
          const r = await api.get("/me/family");
          id = r.data.family_id;
          const pending = localStorage.getItem("handoff_elig_pending");
          if (pending) {
            await api.post("/eligibility/save", { answers: JSON.parse(pending) });
            localStorage.removeItem("handoff_elig_pending");
          }
        }
        await load(id);
        setFid(id);
      } catch (e) {
        setError(e?.response?.status || 500);
      }
    };
    boot();
  }, [mode, load]);

  const refresh = useCallback(() => (fid ? load(fid) : Promise.resolve()), [fid, load]);

  const resetDemo = useCallback(() => {
    store.clearDemo();
    window.location.href = "/demo";
  }, []);

  const value = useMemo(() => {
    const roles = data?.roles || [];
    const helpers = data?.helpers || [];
    const helper =
      helpers.find((h) => h.id === helperId) || helpers.find((h) => h.status === "ready") || helpers[0] || null;
    const p = {
      home: base,
      helperHome: mode === "helper" ? base : `${base}/helper`,
    };
    return { mode, base, fid, data, error, refresh, resetDemo, roles, helper, paths: p, isDemo: mode === "demo" };
  }, [mode, base, fid, data, error, refresh, resetDemo, helperId]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
